/**
 * TwemojiText — Renderiza emojis como SVGs vectoriales locales (Twemoji).
 * Los SVGs se sirven desde /public/twemoji/ sin depender de CDN externo.
 */

import { memo, useMemo } from 'react';

/** Convierte un emoji a su código Unicode (ej: 👍 → "1f44d") */
function emojiToCodepoint(emoji: string): string {
  const codepoints: string[] = [];
  for (const char of emoji) {
    const cp = char.codePointAt(0);
    if (cp !== undefined && cp !== 0xfe0f) {
      codepoints.push(cp.toString(16));
    }
  }
  // Variante con fe0f para emojis compuestos
  const withFe0f: string[] = [];
  for (const char of emoji) {
    const cp = char.codePointAt(0);
    if (cp !== undefined) {
      withFe0f.push(cp.toString(16));
    }
  }
  return codepoints.join('-');
}

// Regex para detectar emojis en texto
const EMOJI_REGEX = /(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(?:\u200D(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F))*/gu;

interface TwemojiTextProps {
  children: string;
  style?: React.CSSProperties;
  className?: string;
  /** Tamaño del emoji en px (default 20) */
  size?: number;
}

export const TwemojiText = memo(function TwemojiText({ children, style, className, size = 20 }: TwemojiTextProps) {
  const parts = useMemo(() => {
    const result: Array<{ type: 'text'; value: string } | { type: 'emoji'; value: string; code: string }> = [];
    let lastIndex = 0;

    for (const match of children.matchAll(EMOJI_REGEX)) {
      const idx = match.index!;
      if (idx > lastIndex) {
        result.push({ type: 'text', value: children.slice(lastIndex, idx) });
      }
      result.push({ type: 'emoji', value: match[0], code: emojiToCodepoint(match[0]) });
      lastIndex = idx + match[0].length;
    }
    if (lastIndex < children.length) {
      result.push({ type: 'text', value: children.slice(lastIndex) });
    }
    return result;
  }, [children]);

  return (
    <span style={{ ...style, display: 'inline-flex', alignItems: 'center' }} className={className}>
      {parts.map((p, i) =>
        p.type === 'text' ? (
          <span key={i}>{p.value}</span>
        ) : (
          <img
            key={i}
            src={`/twemoji/${p.code}.svg`}
            alt={p.value}
            draggable={false}
            style={{
              width: size,
              height: size,
              verticalAlign: 'middle',
              display: 'inline-block',
            }}
            // Fallback a emoji nativo si no existe el SVG local
            onError={(e) => {
              const span = document.createElement('span');
              span.textContent = p.value;
              span.style.fontSize = `${size}px`;
              span.style.lineHeight = '1';
              (e.target as HTMLElement).replaceWith(span);
            }}
          />
        ),
      )}
    </span>
  );
});
